import { $isLinkNode } from '@lexical/link';
import { ListNode } from '@lexical/list';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isHeadingNode, $isQuoteNode } from '@lexical/rich-text';
import { $findMatchingParent, $getNearestNodeOfType, mergeRegister } from '@lexical/utils';
import type { ElementFormatType } from 'lexical';
import { $getSelection, $isElementNode, $isRangeSelection, $isRootOrShadowRoot, COMMAND_PRIORITY_LOW, SELECTION_CHANGE_COMMAND } from 'lexical';
import { useCallback, useEffect, useState } from 'react';

import { $getListNumbering } from '../list-numbering/listNumbering';

/** The block the caret sits in, in the vocabulary the toolbar's paragraph/list controls use. */
export type BlockType = 'paragraph' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'quote' | 'bullet' | 'number' | 'hierarchical' | 'check';

export type ToolbarState = {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strikethrough: boolean;
  subscript: boolean;
  superscript: boolean;
  code: boolean;
  isLink: boolean;
  blockType: BlockType;
  alignment: ElementFormatType;
};

const INITIAL_STATE: ToolbarState = {
  bold: false,
  italic: false,
  underline: false,
  strikethrough: false,
  subscript: false,
  superscript: false,
  code: false,
  isLink: false,
  blockType: 'paragraph',
  alignment: '',
};

/**
 * Mirrors the caret's formatting into React state so toolbar buttons can light up the way
 * Froala's `fr-active` does. Recomputed on every selection change and on every update, because
 * a remote (collaborative) edit can move the caret without a local selection event.
 */
export const useToolbarState = (): ToolbarState => {
  const [editor] = useLexicalComposerContext();
  const [state, setState] = useState<ToolbarState>(INITIAL_STATE);

  const read = useCallback(() => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) return;

    const anchorNode = selection.anchor.getNode();
    const element = $findMatchingParent(anchorNode, (node) => $isElementNode(node) && !node.isInline() && !$isRootOrShadowRoot(node));
    const block = element ?? anchorNode.getTopLevelElementOrThrow();

    // The caret's block in a list is its item, so the list is looked up separately
    const list = $getNearestNodeOfType(anchorNode, ListNode);

    let blockType: BlockType = 'paragraph';
    if (list) {
      blockType = list.getListType();
      if (blockType === 'number' && $getListNumbering(anchorNode) === 'hierarchical') blockType = 'hierarchical';
    } else if ($isHeadingNode(block)) {
      blockType = block.getTag();
    } else if ($isQuoteNode(block)) {
      blockType = 'quote';
    }

    // A link wraps the text, so look upwards from the caret rather than at the block.
    const isLink = !!$findMatchingParent(anchorNode, (node) => $isLinkNode(node));

    setState({
      bold: selection.hasFormat('bold'),
      italic: selection.hasFormat('italic'),
      underline: selection.hasFormat('underline'),
      strikethrough: selection.hasFormat('strikethrough'),
      subscript: selection.hasFormat('subscript'),
      superscript: selection.hasFormat('superscript'),
      code: selection.hasFormat('code'),
      isLink,
      blockType,
      alignment: $isElementNode(block) ? block.getFormatType() : '',
    });
  }, []);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => editorState.read(read)),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          read();
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
    );
  }, [editor, read]);

  return state;
};
